import { useQuery } from "@tanstack/react-query"
import {
  Link,
  useLocation,
  useMatchRoute,
  useRouterState,
} from "@tanstack/react-router"
import { useEffect } from "react"
import {
  Dialog,
  ModalOverlay,
  ModalOverlayProps,
  Modal as RACModal,
} from "react-aria-components"
import { useTranslation } from "react-i18next"

import api from "@/utilities/api"

import { Button, IconButton, LinkButton } from "@/globalComponents/atoms/Button"
import Heading from "@/globalComponents/atoms/Heading"
import Icon from "@/globalComponents/atoms/Icon"
import Separator from "@/globalComponents/atoms/Separator"
import Text from "@/globalComponents/atoms/Text"
import { ENV } from "@/utilities/config"
import { upperFirst } from "@/utilities/etc"
import { RATES_HREF } from "@/utilities/globals"
import { meQueryOptions } from "@/utilities/hooks/queries/user"
import { useTheme } from "@/utilities/hooks/useTheme"

export function Drawer(props: ModalOverlayProps) {
  const { t } = useTranslation()
  const { scannedArea } = useTheme()
  const { data: me } = useQuery(meQueryOptions())
  const routerState = useRouterState()
  const location = useLocation()

  const isAuthenticated =
    routerState.matches[0]?.context?.isAuthenticated || false

  const matchRoute = useMatchRoute()
  const isVehicleRoute = !!matchRoute({ to: "/account/vehicles", fuzzy: true })

  useEffect(() => {
    if (routerState.status === "pending" && props.isOpen === true) {
      props.onOpenChange?.(false)
    }
  }, [routerState.status, props.isOpen])

  return (
    <ModalOverlay className="nav-drawer--overlay" isDismissable {...props}>
      <RACModal className="nav-drawer">
        <Dialog className="nav-drawer--inner">
          <div className="nav-drawer--header">
            <Heading level={2} slot="title">
              {isAuthenticated && me?.cognitoUser
                ? t("navigation.drawer.authenticatedHeader", {
                    firstName: me.cognitoUser.given_name,
                  })
                : t("navigation.drawer.header")}
            </Heading>
            <IconButton
              hue="primary"
              presentation="ghost"
              iconName="close"
              aria-label={t("common.close")}
              onPress={() => props.onOpenChange?.(false)}
            />
          </div>
          <div className="nav-drawer--links">
            <ul>
              {scannedArea?.qrLookupID ? (
                <li className="body--1">
                  <Link
                    to="/qr/$qrLookupID/welcome"
                    params={{ qrLookupID: scannedArea.qrLookupID }}
                  >
                    <Icon name="home" slot="left" />
                    {t("common.home")}
                  </Link>
                </li>
              ) : (
                <li className="body--1">
                  <Link to="/">
                    <Icon name="home" slot="left" />
                    {t("common.home")}
                  </Link>
                </li>
              )}
              <li className="body--1">
                <a href={RATES_HREF} target="_blank">
                  <Icon name="moneySymbol" slot="left" />
                  {t("navigation.drawer.rates")}
                </a>
              </li>
              <li className="body--1">
                {!!scannedArea ? (
                  <Link
                    to="/qr/$qrLookupID/help"
                    params={{ qrLookupID: scannedArea?.qrLookupID }}
                  >
                    <Icon name="question" slot="left" />
                    {t("navigation.drawer.help")}
                  </Link>
                ) : (
                  <Link to="/help">
                    <Icon name="question" slot="left" />
                    {t("navigation.drawer.help")}
                  </Link>
                )}
              </li>
              <li className="body--1">
                <Link
                  to="/settings/language"
                  search={{ qrLookupID: scannedArea?.qrLookupID }}
                >
                  <Icon name="translation" slot="left" />
                  {t("navigation.drawer.languages")}
                </Link>
              </li>
            </ul>
            {isAuthenticated && (
              <>
                <Separator
                  orientation="horizontal"
                  style={{ marginLeft: "var(--space-4)" }}
                />
                <ul>
                  <li className="body--1">
                    <Link
                      to="/account"
                      search={{ qrLookupID: scannedArea?.qrLookupID }}
                      activeOptions={{ exact: isVehicleRoute ? true : false }}
                    >
                      <Icon name="person" slot="left" />
                      {t("navigation.drawer.account")}
                    </Link>
                  </li>
                  <li className="body--1">
                    <Link
                      to="/account/vehicles"
                      search={{ qrLookupID: scannedArea?.qrLookupID }}
                    >
                      <Icon name="car" slot="left" />
                      {t("navigation.drawer.vehicles")}
                    </Link>
                  </li>
                </ul>
              </>
            )}
          </div>
          {
            <div className="nav-drawer--footer">
              {isAuthenticated ? (
                <Button
                  onPress={() => api.auth.signOut()}
                  hue="danger"
                  presentation="ghost"
                >
                  <Icon name="logOut" slot="left" />
                  Sign out
                </Button>
              ) : (
                <>
                  <LinkButton
                    hue="neutral"
                    variant="default"
                    to="/auth/sign-in"
                    search={{
                      redirect: location.href,
                      qrLookupID: scannedArea?.qrLookupID,
                    }}
                  >
                    Sign in
                  </LinkButton>
                  <LinkButton
                    hue="secondary"
                    variant="default"
                    to="/auth/create-account"
                    search={{
                      redirect: location.href,
                      qrLookupID: scannedArea?.qrLookupID,
                    }}
                  >
                    Create Account
                  </LinkButton>
                </>
              )}
            </div>
          }
          {me && (
            <Text
              level={3}
              className="nav-drawer--app-version"
              style={{ paddingBottom: "var(--space-1)" }}
            >
              {t("navigation.drawer.loggedInAs", {
                email: me.cognitoUser.email,
              })}
            </Text>
          )}
          <Text level={3} className="nav-drawer--app-version">
            {ENV !== "production" && `${upperFirst(ENV)} `}Version {APP_VERSION}
          </Text>
        </Dialog>
      </RACModal>
    </ModalOverlay>
  )
}

export default Drawer
